'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormData = z.infer<typeof formSchema>;

interface SendOTPFormProps {
  onOtpSent?: (email: string) => void; // Make optional for backward compatibility
}

export function SendOTPForm({ onOtpSent }: SendOTPFormProps) {
  const router = useRouter();
  const { sendOTP, otpState } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = useCallback(async (data: FormData) => {
    try {
      // sendOTP now returns a boolean indicating success
      const success = await sendOTP(data.email);
      
      // Only navigate to verify OTP if sendOTP was successful
      if (success) {
        if (onOtpSent) {
          // Backward compatibility: use callback if provided
          onOtpSent(data.email);
        } else {
          // New flow: redirect to verify OTP page
          router.push(`/verify-otp?email=${encodeURIComponent(data.email)}&from=login`);
        }
      }
    } catch {
      // Error handling is done in the context
    }
  }, [sendOTP, onOtpSent, router]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium text-foreground">
          Work Email
        </Label>
        <Input
          {...register('email')}
          id="email"
          type="email"
          placeholder="name@company.com"
          className="h-11 border-gray-200 dark:border-gray-600 focus:border-orange-500 focus:ring-orange-500/20 transition-colors"
        />
        {errors.email && (
          <p className="text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>
      
      {/* Display error from context state */}
      {otpState.error && (
        <div className="pt-2">
          <p className="text-sm text-red-500">{otpState.error}</p>
        </div>
      )}
      
      <div className="pt-2">
        <Button 
          type="submit" 
          className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200" 
          disabled={otpState.isLoading}
          size="lg"
        >
          {otpState.isLoading ? 'Sending OTP...' : 'Send OTP'}
        </Button>
      </div>
    </form>
  );
}
